import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { orgsQuery } from "../lib/store";
import { TopBar } from "../components/Shell";
import { Badge, Banner, Button, Card, Field, Input, Skeleton, Textarea, useToast } from "../components/ui";
import { PartnerApps } from "./PartnerApps";

type PStore = { id: string; handle: string; name: string; status: string; is_development: boolean };
type PRequest = { id: string; store_name?: string; store_handle?: string; status: string; permissions: string[]; created_at: string };
type PTransfer = { id: string; store_name: string; to_email: string; status: string; created_at: string };

const requestable = ["products.view", "products.edit", "online_store.themes.view", "online_store.themes.customize", "online_store.themes.edit_code", "online_store.content", "orders.view", "customers.view", "analytics.view"];

export function PartnerDashboard() {
  const { t, date } = useI18n();
  const { org } = useParams({ strict: false }) as { org: string };
  const orgs = useQuery(orgsQuery);
  const me = orgs.data?.find((o) => o.id === org);
  const base = `/admin/v1/partners/${org}`;
  const stores = useQuery({ queryKey: ["partner", org, "stores"], queryFn: () => api<{ stores: PStore[] }>(`${base}/stores`) });
  const requests = useQuery({ queryKey: ["partner", org, "requests"], queryFn: () => api<{ requests: PRequest[] }>(`${base}/collaborator-requests`) });
  const transfers = useQuery({ queryKey: ["partner", org, "transfers"], queryFn: () => api<{ transfers: PTransfer[] }>(`${base}/transfers`) });

  return (
    <div className="min-h-full">
      <TopBar />
      <div className="mx-auto max-w-4xl space-y-4 px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{me?.name ?? t("partner.title")}</h1>
            <p className="text-sm text-stone-600">{t("partner.title")}</p>
          </div>
          <Link to="/stores/new" search={{ org }}><Button variant="primary">{t("partner.dev_store")}</Button></Link>
        </div>
        {me?.partner_status === "pending" && <Banner tone="info">{t("partner.pending")}</Banner>}
        {me?.referral_code && <p className="text-sm text-stone-600">{t("partner.referral")}: <code dir="ltr" className="rounded bg-stone-200 px-1.5 py-0.5">{me.referral_code}</code></p>}

        <Card title={t("partner.stores")}>
          {stores.isPending ? <Skeleton /> : stores.data?.stores.length === 0 ? <p className="text-sm text-stone-500">{t("common.none")}</p> : (
            <ul className="-m-2 divide-y divide-stone-100">
              {stores.data?.stores.map((s) => (
                <li key={s.id}>
                  <Link to="/store/$handle" params={{ handle: s.handle }} className="flex items-center justify-between rounded-lg p-3 hover:bg-stone-50">
                    <span className="font-medium">{s.name}</span>
                    {s.is_development && <Badge tone="info">{t("chooser.development")}</Badge>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <RequestAccess org={org} />

        <PartnerApps org={org} approved={me?.partner_status === "approved"} 
          canManage={["owner", "admin", "builder"].includes(me?.role ?? "")} canAdmin={["owner", "admin"].includes(me?.role ?? "")} />

        <div className="grid gap-4 md:grid-cols-2">
          <Card title={t("partner.requests")}>
            <ul className="space-y-2 text-sm">
              {requests.data?.requests.length === 0 && <li className="text-stone-500">{t("common.none")}</li>}
              {requests.data?.requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{r.store_name ?? r.store_handle}</span>
                  <Badge tone={r.status === "approved" ? "success" : r.status === "pending" ? "warning" : "neutral"}>{r.status}</Badge>
                </li>
              ))}
            </ul>
          </Card>
          <Card title={t("partner.transfers")}>
            <ul className="space-y-2 text-sm">
              {transfers.data?.transfers.length === 0 && <li className="text-stone-500">{t("common.none")}</li>}
              {transfers.data?.transfers.map((x) => (
                <li key={x.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{x.store_name} → <span dir="ltr">{x.to_email}</span></span>
                  <span className="flex items-center gap-2"><span className="text-xs text-stone-500">{date(x.created_at)}</span>
                    <Badge tone={x.status === "accepted" ? "success" : x.status === "pending" ? "warning" : "neutral"}>{x.status}</Badge></span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
        <Card title={t("partner.earnings")}><p className="text-sm text-stone-600">{t("partner.earnings_soon")}</p></Card>
      </div>
    </div>
  );
}

function RequestAccess({ org }: { org: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const [domain, setDomain] = useState("");
  const [perms, setPerms] = useState<string[]>(["products.view"]);
  const [message, setMessage] = useState("");
  const send = useMutation({
    mutationFn: () => api(`/admin/v1/partners/${org}/collaborator-requests`, { body: { store_domain: domain, permissions: perms, message } }),
    onSuccess: () => { toast(t("common.saved")); setDomain(""); setMessage(""); qc.invalidateQueries({ queryKey: ["partner", org, "requests"] }); },
  });
  return (
    <Card title={t("partner.request")}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); send.mutate(); }}>
        {send.error && <Banner tone="critical">{send.error.message}</Banner>}
        <Field label={t("partner.store_domain")}><Input dir="ltr" required value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={`shop.${import.meta.env.VITE_STOREFRONT_DOMAIN}`} /></Field>
        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t("partner.permissions")}</legend>
          <div className="grid gap-1 sm:grid-cols-2">
            {requestable.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm" dir="ltr">
                <input type="checkbox" checked={perms.includes(p)} onChange={(e) => setPerms(e.target.checked ? [...perms, p] : perms.filter((x) => x !== p))} />{p}
              </label>
            ))}
          </div>
        </fieldset>
        <Field label={t("partner.message")} optional><Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} /></Field>
        <Button variant="primary" type="submit" loading={send.isPending} disabled={perms.length === 0}>{t("partner.send_request")}</Button>
      </form>
    </Card>
  );
}
